import React, { Component, ErrorInfo, ReactNode } from "react";
import { Box, Button, Heading, Text, Center, Flex } from "@chakra-ui/react";
import { FiAlertTriangle } from "react-icons/fi";

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public override state: ErrorBoundaryState = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("Uncaught route error:", error, errorInfo);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Center h="400px" w="100%">
          <Box
            bg="white"
            p={8}
            borderRadius="2xl"
            borderWidth="1px"
            borderColor="gray.100"
            boxShadow="sm"
            textAlign="center"
            maxW="500px"
            w="100%"
          >
            <Flex
              h="64px"
              w="64px"
              mx="auto"
              mb={4}
              align="center"
              justify="center"
              borderRadius="full"
              bg="red.50"
              color="red.500"
            >
              <FiAlertTriangle size={32} />
            </Flex>
            <Heading as="h2" size="md" color="gray.800" fontWeight="bold">
              Something went wrong loading this page
            </Heading>
            <Text mt={2} fontSize="sm" color="gray.500">
              {this.state.error?.message || "An unexpected error occurred while rendering this section."}
            </Text>
            <Button
              mt={6}
              onClick={this.handleReset}
              bg="#2ead81"
              color="white"
              borderRadius="xl"
              px={6}
              py={2.5}
              fontSize="sm"
              fontWeight="bold"
              _hover={{ bg: "#25966f" }}
            >
              Reload Page
            </Button>
          </Box>
        </Center>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
